import React, { useEffect, useId, useRef } from 'react';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import useConfirmStore, { dismissResultFor } from '../stores/confirmStore';
import { registerModal } from '../utils/modalStack';

// The choice mode's third action: red by default (a throw-away), neutral when it keeps the work.
const DISCARD_STYLES = {
  danger: 'text-red-700 dark:text-red-400 border-red-300 dark:border-red-800 hover:bg-red-50 focus:ring-red-500',
  neutral: 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 focus:ring-gray-400',
};

const VARIANT_STYLES = {
  danger: {
    icon: <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />,
    iconBg: 'bg-red-100',
    confirmBtn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white',
  },
  warning: {
    icon: <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />,
    iconBg: 'bg-amber-100',
    confirmBtn: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500 text-white',
  },
  primary: {
    icon: <InformationCircleIcon className="w-6 h-6 text-indigo-500" />,
    iconBg: 'bg-indigo-100',
    confirmBtn: 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 text-white',
  },
};

export default function ConfirmModal() {
  const {
    isOpen,
    title,
    message,
    confirmLabel,
    cancelLabel,
    discardLabel,
    discardTone,
    variant,
    mode,
    initialFocus,
    openId,
    inputValue,
    placeholder,
    setInputValue,
    close,
  } = useConfirmStore();
  const titleId = useId();
  const confirmRef = useRef(null);
  const cancelRef = useRef(null);
  const inputRef = useRef(null);

  const isPrompt = mode === 'prompt';
  const isChoice = mode === 'choice';
  // Cancel/dismiss result: null for a prompt (no value), 'keep' for a choice,
  // false otherwise.
  const dismissResult = dismissResultFor(mode);
  const confirmResult = isPrompt ? inputValue : isChoice ? 'save' : true;

  // Focus the input in prompt mode; otherwise the button `initialFocus` names
  // (default: confirm). A dialog with no cancel button (alert) falls back to confirm.
  // `openId` re-runs this when one dialog replaces another without closing first.
  useEffect(() => {
    if (!isOpen) return;
    if (isPrompt && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    } else if (initialFocus === 'cancel' && cancelRef.current) {
      cancelRef.current.focus();
    } else if (confirmRef.current) {
      confirmRef.current.focus();
    }
  }, [isOpen, isPrompt, initialFocus, openId]);

  // Close on Escape — but only when this is the topmost overlay. A Modal under
  // this dialog must survive the Escape that dismisses the dialog (modalStack.js).
  // The entry reads the live dismiss result through a ref, so re-registering on a
  // mode change never reorders the stack.
  const dismissRef = useRef(dismissResult);
  dismissRef.current = dismissResult;
  useEffect(() => {
    if (!isOpen) return undefined;
    const entry = registerModal(() => close(dismissRef.current));
    return entry.unregister;
  }, [isOpen, close]);

  if (!isOpen) return null;

  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.danger;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop dismiss handled by Escape key listener */}
      <div className="fixed inset-0 bg-black/40 transition-opacity" onClick={() => close(dismissResult)} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className={`flex-shrink-0 w-10 h-10 rounded-full ${styles.iconBg} flex items-center justify-center`}>
              {styles.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </h3>
              {message && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">{message}</p>
              )}
              {isPrompt && (
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  placeholder={placeholder}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') close(inputValue);
                  }}
                  className="mt-3 w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                />
              )}
            </div>
          </div>
        </div>

        {/* Actions. `flex-wrap` lets a third action drop to a second line at phone width;
            a 44px target on a narrow window (`max-sm`) AND on any touch-first device whatever its width
            (`pointer-coarse`: a tablet is wider than `sm`), compact for a mouse on a wide window. */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex flex-wrap justify-end gap-3">
          {mode !== 'alert' && (
            <button
              ref={cancelRef}
              type="button"
              onClick={() => close(dismissResult)}
              className="max-sm:min-h-11 pointer-coarse:min-h-11 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-all"
            >
              {cancelLabel}
            </button>
          )}
          {isChoice && (
            <button
              type="button"
              onClick={() => close('discard')}
              className={`max-sm:min-h-11 pointer-coarse:min-h-11 px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border rounded-lg dark:hover:bg-gray-800 focus:outline-hidden focus:ring-2 focus:ring-offset-2 transition-all ${DISCARD_STYLES[discardTone] || DISCARD_STYLES.danger}`}
            >
              {discardLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            onClick={() => close(confirmResult)}
            className={`max-sm:min-h-11 pointer-coarse:min-h-11 px-4 py-2 text-sm font-medium rounded-lg focus:outline-hidden focus:ring-2 focus:ring-offset-2 transition-all ${styles.confirmBtn}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
